/**
 * Live data adapter for production API calls
 */

import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { DataSourceAdapter, DataSourceConfig, ApiResponse, ApiError, RequestConfig } from '../../types';
import { deserializeJsonApi, isJsonApiFormat } from '../../../jsonapi/deserializer';
import { getProviderDomain } from '../../../provider-context';
import { logger } from '../../../logger';
import { API_BASE_URL, API_PROXY_PREFIX, API_PROXY_DISABLE_REWRITE, API_TARGET_URL, USING_DEV_PROXY } from '../../base-url';

type AuthScope = 'provider' | 'customer';

type ScopedAxiosRequestConfig = InternalAxiosRequestConfig & {
  florentinAuthScope?: AuthScope;
};

const SENSITIVE_KEYS = new Set(['client_secret', 'clientSecret']);

function redactSensitive(input: unknown): unknown {
  if (!input || typeof input !== 'object') {
    return input;
  }

  if (Array.isArray(input)) {
    return input.map(redactSensitive);
  }

  const record = input as Record<string, unknown>;
  const next: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(record)) {
    if (SENSITIVE_KEYS.has(key)) {
      next[key] = '[REDACTED]';
      continue;
    }
    next[key] = redactSensitive(value);
  }

  return next;
}

export class LiveAdapter implements DataSourceAdapter {
  public readonly source = 'live' as const;
  private axiosInstance: AxiosInstance;
  
  constructor(_config: DataSourceConfig) {
    // Create axios instance
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL || API_TARGET_URL,
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

      logger.info(
          'test',
          this.axiosInstance.defaults.baseURL
      );
    
    this.setupInterceptors();
    
    // const context = USING_DEV_PROXY
    //   ? {
    //       proxyPrefix: API_PROXY_PREFIX || '/',
    //       disableRewrite: API_PROXY_DISABLE_REWRITE,
    //       baseURL: this.axiosInstance.defaults.baseURL,
    //       targetBase: API_TARGET_URL,
    //     }
    //   : { baseURL: this.axiosInstance.defaults.baseURL };

    const context = { baseURL: this.axiosInstance.defaults.baseURL };
    logger.info(
      '%c🌐 Live Data Adapter initialized',
      'color: #3b82f6; font-weight: bold;',
      context
    );
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const scopedConfig = config as ScopedAxiosRequestConfig;
        const scope = this.resolveAuthScope(scopedConfig);
        scopedConfig.florentinAuthScope = scope;

        config.headers = config.headers ?? {};
        // Signal scope to the API so it can select the correct guard/tenant logic
        if (!config.headers['X-Florentin-Auth-Scope']) {
          config.headers['X-Florentin-Auth-Scope'] = scope;
        }

        if (
          USING_DEV_PROXY &&
          API_PROXY_PREFIX &&
          !API_PROXY_DISABLE_REWRITE &&
          config.url &&
          !/^https?:/i.test(config.url)
        ) {
          const hasPrefix = config.url.startsWith(API_PROXY_PREFIX);
          if (!hasPrefix) {
            const normalizedPath = config.url.startsWith('/') ? config.url : `/${config.url}`;
            const combined = `${API_PROXY_PREFIX}${normalizedPath}`;
            config.url = combined.replace(`${API_PROXY_PREFIX}//`, `${API_PROXY_PREFIX}/`);
          }
        }

        // Add auth token if available
        const token = this.getAuthToken(scope);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        this.unsetHeader(config.headers, 'X-Provider-Slug');
        const providerDomain = this.resolveProviderDomainHeader();
        if (providerDomain) {
          config.headers['X-Provider-Domain'] = providerDomain;
        } else {
          this.unsetHeader(config.headers, 'X-Provider-Domain');
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const scopedConfig = error.config as ScopedAxiosRequestConfig | undefined;
        const scope = scopedConfig?.florentinAuthScope ?? this.getScopeFromLocation();
        if (error.response) {
          const method = (scopedConfig?.method || 'get').toUpperCase();
          const url = scopedConfig?.url || 'unknown';
          const status = error.response.status;
          const dataPreview =
            typeof error.response.data === 'object'
              ? redactSensitive(error.response.data)
              : String(error.response.data);
          logger.warn('[LiveAdapter] Request failed', { method, url, status, scope, dataPreview });
        }
        // Handle 401 unauthorized
        const ignore401 =
          scopedConfig?.url?.includes('/auth/provider/oauth/providers') ||
          scopedConfig?.url?.includes('/auth/customer/oauth/providers');
        if (error.response?.status === 401 && !ignore401) {
          this.handleUnauthorized(scope);
        }
        
        return Promise.reject(error);
      }
    );
  }

  private resolveAuthScope(config: ScopedAxiosRequestConfig): AuthScope {
    if (config.florentinAuthScope) {
      return config.florentinAuthScope;
    }

    const headers = config.headers;
    let headerScope: unknown;

    if (headers) {
      if (typeof (headers as { get?: (name: string) => unknown }).get === 'function') {
        headerScope = (headers as { get: (name: string) => unknown }).get('X-Florentin-Auth-Scope');
      } else {
        headerScope = (headers as Record<string, unknown>)['X-Florentin-Auth-Scope'];
      }
    }

    if (headerScope === 'customer' || headerScope === 'provider') {
      return headerScope;
    }

    const url = config.url ?? '';
    if (/\/api(?:\/v1)?\/auth\/customer(\/|$)/.test(url) || /\/api(?:\/v1)?\/customer(\/|$)/.test(url)) {
      return 'customer';
    }

    return this.getScopeFromLocation();
  }

  private getScopeFromLocation(): AuthScope {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/customer')) {
      return 'customer';
    }
    return 'provider';
  }

  private getAuthToken(scope: AuthScope): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const domain = window.location.hostname;
    const lookupOrder = scope === 'customer'
      ? ([`customer_auth_token_${domain}`, 'customer_auth_token'] as const)
      : ([`auth_token_${domain}`, 'auth_token'] as const);

    for (const key of lookupOrder) {
      const token = localStorage.getItem(key);
      if (token) {
        return token;
      }
    }

    return null;
  }

  private handleUnauthorized(scope: AuthScope): void {
    if (typeof window === 'undefined') {
      return;
    }

    const domain = window.location.hostname;
    const keys = scope === 'customer'
      ? ([`customer_auth_token_${domain}`, 'customer_auth_token'] as const)
      : ([`auth_token_${domain}`, 'auth_token'] as const);

    for (const key of keys) {
      localStorage.removeItem(key);
    }

    const currentPath = window.location.pathname;
    const shouldRedirect =
      (scope === 'customer' && currentPath.startsWith('/customer')) ||
      (scope === 'provider' && currentPath.startsWith('/provider'));

    if (!shouldRedirect) {
      return;
    }

    const target = scope === 'customer' ? '/customer/login' : '/provider/login';
    if (currentPath !== target) {
      window.location.href = target;
    }
  }

  private resolveProviderDomainHeader(): string | null {
    const stored = getProviderDomain();
    if (stored) {
      return stored;
    }

    const envOverride = import.meta.env.VITE_PROVIDER_DOMAIN_HEADER;
    if (envOverride && typeof envOverride === 'string') {
      return envOverride;
    }

    if (typeof window !== 'undefined') {
      return window.location.hostname;
    }

    return null;
  }

  private unsetHeader(
    headers: InternalAxiosRequestConfig['headers'] | undefined,
    headerName: string
  ): void {
    if (!headers) {
      return;
    }

    if (typeof (headers as { delete?: (name: string) => void }).delete === 'function') {
      (headers as { delete: (name: string) => void }).delete(headerName);
      return;
    }

    delete (headers as Record<string, unknown>)[headerName];
  }

  async get<T = Record<string, unknown>>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.get(url, config);
      return this.transformResponse<T>(response);
    } catch (error) {
      throw this.transformError(error as AxiosError);
    }
  }

  async post<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.post(url, data, config);
      return this.transformResponse<T>(response);
    } catch (error) {
      throw this.transformError(error as AxiosError);
    }
  }

  async put<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.put(url, data, config);
      return this.transformResponse<T>(response);
    } catch (error) {
      throw this.transformError(error as AxiosError);
    }
  }

  async patch<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.patch(url, data, config);
      return this.transformResponse<T>(response);
    } catch (error) {
      throw this.transformError(error as AxiosError);
    }
  }

  async delete<T = Record<string, unknown>>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.delete(url, config);
      return this.transformResponse<T>(response);
    } catch (error) {
      throw this.transformError(error as AxiosError);
    }
  }

  private transformResponse<T>(response: AxiosResponse): ApiResponse<T> {
    logger.debug('🔄 LiveAdapter transforming response:', redactSensitive(response.data));
    
    let transformedData: unknown;
    const meta = (response.data as { meta?: Record<string, unknown> }).meta;
    
    // Use ts-japi deserializer if it's JSON:API format
    if (isJsonApiFormat(response.data)) {
      logger.debug('🔄 Detected JSON:API format, using ts-japi deserializer...');
      transformedData = deserializeJsonApi<T>(response.data);
      
      // If it's a list response, ensure we maintain the expected structure
      const typedTransformed = transformedData as Record<string, unknown>;
      if (typedTransformed.data && Array.isArray(typedTransformed.data)) {
        logger.debug('🔄 JSON:API list response transformed:', transformedData);
        return {
          data: transformedData as T,
          status: response.status,
          message: (response.data.meta as Record<string, unknown>)?.message as string || 'Success',
          ...(meta ? { meta } : {})
        };
      } else {
        // Single resource response - deserializer already flattened it
        logger.debug('🔄 JSON:API single resource transformed:', transformedData);
        return {
          data: transformedData as T,
          status: response.status,
          message: (response.data.meta as Record<string, unknown>)?.message as string || 'Success',
          ...(meta ? { meta } : {})
        };
      }
    } else {
      // Fallback to original logic for non-JSON:API responses
      transformedData = response.data.data || response.data;
      
      // Legacy manual transformation (keeping for backward compatibility)
      const dataArray = transformedData as Record<string, unknown>[];
      const singleData = transformedData as Record<string, unknown>;
      
      if (Array.isArray(transformedData) && dataArray[0]?.type && dataArray[0]?.attributes) {
        logger.debug('🔄 Fallback: Detected JSON:API format, transforming manually...');
        transformedData = dataArray.map(item => ({
          id: item.id,
          ...(item.attributes as Record<string, unknown>)
        }));
      } else if (singleData?.type && singleData?.attributes) {
        logger.debug('🔄 Fallback: Detected single JSON:API resource, transforming manually...');
        transformedData = {
          id: singleData.id,
          ...(singleData.attributes as Record<string, unknown>)
        };
      }
    }
    
    logger.debug('🔄 Final transformed data:', redactSensitive(transformedData));
    
    return {
      data: transformedData as T,
      status: response.status,
      message: response.data.message || response.data.meta?.message || 'Success',
      ...(meta ? { meta } : {})
    };
  }

  private transformError(error: AxiosError): ApiError {
    if (error.response) {
      const responseData = error.response.data as Record<string, unknown>;
      return {
        message: (responseData?.message as string) || 'Request failed',
        status: error.response.status,
        ...(responseData?.errors ? { errors: responseData.errors as Record<string, string[]> } : {}),
        ...(responseData?.meta && typeof responseData.meta === 'object'
          ? { meta: responseData.meta as Record<string, unknown> }
          : {}),
        payload: responseData,
      };
    }
    
    if (error.request) {
      return {
        message: 'Network error - please check your connection',
        status: 0
      };
    }
    
    return {
      message: error.message || 'Unknown error occurred',
      status: 500
    };
  }
}
